import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ROUTES } from '@/utils/constants'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useTranslation } from 'react-i18next'
import { useUserStore } from '@/stores/userStore'
import { useGroupStore } from '@/stores/groupStore'
import {
  Box,
  Typography,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  Skeleton,
  IconButton,
  TextField,
  InputAdornment,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link as MuiLink,
  Menu,
  MenuItem,
  useMediaQuery,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  Chip,
  TablePagination,
  Collapse,
  Breadcrumbs,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  Add as AddIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  ContentCopy as ContentCopyIcon,
  Send as SendIcon,
  Email as EmailIcon,
  LockReset as LockResetIcon,
  FilterList as FilterListIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import 'dayjs/locale/ru'
import 'dayjs/locale/en'
import { Link as RouterLink } from 'react-router-dom'
import { formatMoney } from '@/components/billing/formatMoney'
import { ContractStatusBadge } from '@/components/contracts/ContractStatusBadge'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { UserCard } from '@/components/users/UserCard'
import { invitationsApi } from '@/api/invitations'
import type { Invitation, UserFilters } from '@/types'
import '@/styles/dashboard.css'

export function UsersPage() {
  const { t, i18n } = useTranslation()
  const adapterLocale = i18n.language === 'ru' ? 'ru' : 'en'
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const {
    users,
    isLoading,
    error,
    pagination,
    fetchUsers,
    deleteUser,
    clearError,
    createUser,
    usersListVersion,
  } = useUserStore()
  const { groups, fetchGroups } = useGroupStore()

  const [searchText, setSearchText] = useState('')
  const [studentsFilters, setStudentsFilters] = useState<Pick<UserFilters, 'contract_status' | 'group_id' | 'sort_by' | 'sort_order'>>({ sort_by: 'id', sort_order: 'desc', contract_status: 'active' })
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [menuUser, setMenuUser] = useState<typeof users[0] | null>(null)
  const [tabValue, setTabValue] = useState(0)
  const [invitations, setInvitations] = useState<Invitation[]>([])
  const [invitationsPagination, setInvitationsPagination] = useState({ page: 1, per_page: 15, total: 0, lastPage: 1 })
  const [invitationsLoading, setInvitationsLoading] = useState(false)
  const [inviteSearchText, setInviteSearchText] = useState('')
  const [inviteModalOpen, setInviteModalOpen] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteSendEmail, setInviteSendEmail] = useState(true)
  const [inviteSubmitting, setInviteSubmitting] = useState(false)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [createdInviteLink, setCreatedInviteLink] = useState<string | null>(null)
  const [invMenuAnchor, setInvMenuAnchor] = useState<null | HTMLElement>(null)
  const [invMenuInvitation, setInvMenuInvitation] = useState<Invitation | null>(null)
  const [createForm, setCreateForm] = useState({
    surname: '',
    first_name: '',
    middle_name: '',
    email: '',
    password: '',
    birthday: '',
    nickname: '',
    notes: '',
  })
  const [createFormError, setCreateFormError] = useState<string | null>(null)
  const [createFormSubscriptionError, setCreateFormSubscriptionError] = useState(false)
  const [createSubmitting, setCreateSubmitting] = useState(false)
  const [passwordResetMessage, setPasswordResetMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [studentsFiltersOpen, setStudentsFiltersOpen] = useState(false)

  const [studentsPerPage, setStudentsPerPage] = useState(15)
  const [invitePerPage, setInvitePerPage] = useState(15)

  useEffect(() => {
    if (usersListVersion === 0) return
    fetchUsers({
      search: searchText.trim() || undefined,
      page: pagination.currentPage,
      per_page: studentsPerPage,
      ...studentsFilters,
    }).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersListVersion])

  useEffect(() => {
    fetchUsers({ per_page: studentsPerPage, page: 1, ...studentsFilters }).catch((err) => {
      console.error('Failed to fetch users:', err)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (tabValue === 0) {
      fetchGroups().catch(() => {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      fetchUsers({
        search: searchText.trim() || undefined,
        page: 1,
        per_page: studentsPerPage,
        ...studentsFilters,
      }).catch(() => {})
    }, 300)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText])

  const applyStudentsFilters = (next: Partial<typeof studentsFilters>) => {
    const merged = { ...studentsFilters, ...next }
    setStudentsFilters(merged)
    fetchUsers({
      page: 1,
      per_page: studentsPerPage,
      search: searchText.trim() || undefined,
      ...merged,
    }).catch(() => {})
  }

  const loadInvitations = (page = 1, perPage = invitePerPage) => {
    setInvitationsLoading(true)
    invitationsApi
      .getInvitations({
        search: inviteSearchText.trim() || undefined,
        page,
        per_page: perPage,
      })
      .then((res) => {
        setInvitations(res.data)
        const lastPage = (res.last_page ?? Math.ceil((res.total || 0) / (res.per_page || perPage))) || 1
        setInvitationsPagination({
          page: res.current_page ?? page,
          per_page: res.per_page ?? perPage,
          total: res.total ?? 0,
          lastPage,
        })
      })
      .catch(() => setInvitations([]))
      .finally(() => setInvitationsLoading(false))
  }

  useEffect(() => {
    if (tabValue !== 1) return
    const timer = window.setTimeout(() => loadInvitations(1), 300)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, inviteSearchText])

  const openMenu = (e: React.MouseEvent<HTMLElement>, user: typeof users[0]) => {
    e.stopPropagation()
    setMenuAnchor(e.currentTarget)
    setMenuUser(user)
  }

  const closeMenu = () => {
    setMenuAnchor(null)
    setMenuUser(null)
  }

  const handleView = () => {
    if (menuUser) navigate(`${ROUTES.USERS}/${menuUser.id}`)
    closeMenu()
  }

  const handleDelete = async (userId?: number) => {
    const id = userId ?? menuUser?.id
    if (id == null) return
    closeMenu()
    if (window.confirm(t('users.deleteConfirm'))) {
      try {
        await deleteUser(id)
        fetchUsers()
      } catch (err) {
        // Error handled in store
      }
    }
  }

  const handleSendPasswordReset = async () => {
    if (!menuUser) return
    const id = menuUser.id
    closeMenu()
    try {
      const { usersApi: api } = await import('@/api/users')
      await api.sendPasswordReset(id)
      setPasswordResetMessage({ type: 'success', text: t('users.sendPasswordResetSuccess') })
      setTimeout(() => setPasswordResetMessage(null), 5000)
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string }
      setPasswordResetMessage({
        type: 'error',
        text: error?.response?.data?.message || error?.message || t('common.error'),
      })
      setTimeout(() => setPasswordResetMessage(null), 5000)
    }
  }

  const getUserDisplayName = (user: typeof users[0]) => {
    if (user.display_name) return user.display_name
    if (user.first_name && user.surname) {
      return `${user.surname} ${user.first_name}`
    }
    return user.email
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      // Could add snackbar
    })
  }

  const handleCreateInvitation = async () => {
    setInviteSubmitting(true)
    setInviteError(null)
    setCreatedInviteLink(null)
    try {
      const inv = await invitationsApi.createInvitation({
        email: inviteEmail || undefined,
        send_email: inviteSendEmail,
      })
      setCreatedInviteLink(inv.invitation_url)
      loadInvitations()
    } catch (err: unknown) {
      const error = err as { message?: string }
      setInviteError(error?.message || t('common.error'))
    } finally {
      setInviteSubmitting(false)
    }
  }

  const handleResendInvitation = async (id: number) => {
    try {
      await invitationsApi.resendInvitation(id)
      loadInvitations()
    } catch (err) {
      console.error(err)
    }
  }

  const handleCancelInvitation = async (id: number) => {
    if (!window.confirm(t('invitations.cancelConfirm'))) return
    try {
      await invitationsApi.deleteInvitation(id)
      loadInvitations()
    } catch (err) {
      console.error(err)
    }
  }

  const closeInviteModal = () => {
    setInviteModalOpen(false)
    setInviteEmail('')
    setInviteSendEmail(true)
    setCreatedInviteLink(null)
    setInviteError(null)
  }

  const handleCreateStudent = async () => {
    if (!createForm.surname.trim() || !createForm.first_name.trim() || !createForm.email.trim() || !createForm.password) {
      setCreateFormError(t('users.createValidationRequired'))
      return
    }
    setCreateSubmitting(true)
    setCreateFormError(null)
    try {
      await createUser({
        surname: createForm.surname.trim(),
        first_name: createForm.first_name.trim(),
        middle_name: createForm.middle_name.trim() || undefined,
        email: createForm.email.trim(),
        password: createForm.password,
        birthday: createForm.birthday || undefined,
        nickname: createForm.nickname.trim() || undefined,
        notes: createForm.notes.trim() || undefined,
      })
      setIsCreateModalOpen(false)
      setCreateForm({ surname: '', first_name: '', middle_name: '', email: '', password: '', birthday: '', nickname: '', notes: '' })
      fetchUsers()
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string; message?: string } }; message?: string }
      const data = error?.response?.data
      const isSubErr = data?.error === 'subscription_limit_exceeded' || data?.error === 'subscription_parameter_denied'
      if (isSubErr) {
        setCreateFormError(data?.message || t('users.subscriptionLimitExceeded'))
        setCreateFormSubscriptionError(true)
      } else {
        setCreateFormError(data?.message || error?.message || t('common.error'))
        setCreateFormSubscriptionError(false)
      }
    } finally {
      setCreateSubmitting(false)
    }
  }

  const closeCreateModal = () => {
    setIsCreateModalOpen(false)
    setCreateForm({ surname: '', first_name: '', middle_name: '', email: '', password: '', birthday: '', nickname: '', notes: '' })
    setCreateFormError(null)
    setCreateFormSubscriptionError(false)
  }

  const getUsedInvitationDisplay = (inv: Invitation) => {
    if (inv.status !== 'used' || !inv.used) return inv.email || t('invitations.noEmail')
    const parts = [inv.used.surname, inv.used.first_name, inv.used.middle_name].filter(Boolean)
    return parts.length ? parts.join(' ') : inv.used.email
  }

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
          <MuiLink component={RouterLink} to={ROUTES.DASHBOARD} underline="hover" sx={{ display: 'flex', alignItems: 'center' }}>
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </MuiLink>
          <Typography color="text.primary">{t('dashboard.students')}</Typography>
        </Breadcrumbs>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {t('users.title')}
          </Typography>
          <PageHelpPopover pageKey="users" />
        </Box>

        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
          <Tab label={t('invitations.tabStudents')} />
          <Tab label={t('invitations.tabInvitations')} />
        </Tabs>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
            {error}
          </Alert>
        )}
        {passwordResetMessage && (
          <Alert
            severity={passwordResetMessage.type}
            sx={{ mb: 2 }}
            onClose={() => setPasswordResetMessage(null)}
          >
            {passwordResetMessage.text}
          </Alert>
        )}

        {tabValue === 0 && (
        <>
        {/* Тулбар: поиск, сортировка, кнопка «Фильтры», кнопка создания */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder={t('users.searchPlaceholder')}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: isMobile ? 120 : 200, flex: isMobile ? 1 : undefined }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>{t('admin.users.sortBy')}</InputLabel>
            <Select
              value={`${studentsFilters.sort_by || 'id'}_${studentsFilters.sort_order || 'desc'}`}
              label={t('admin.users.sortBy')}
              onChange={(e) => {
                const [sort_by, sort_order] = (e.target.value as string).split('_') as [UserFilters['sort_by'], 'asc' | 'desc']
                applyStudentsFilters({ sort_by, sort_order })
              }}
            >
              <MenuItem value="id_desc">ID (↓)</MenuItem>
              <MenuItem value="id_asc">ID (↑)</MenuItem>
              <MenuItem value="surname_asc">{t('profile.surname')} (A–Z)</MenuItem>
              <MenuItem value="surname_desc">{t('profile.surname')} (Z–A)</MenuItem>
              <MenuItem value="created_at_desc">ID / дата (↓)</MenuItem>
              <MenuItem value="created_at_asc">ID / дата (↑)</MenuItem>
            </Select>
          </FormControl>
          <Button
            size="small"
            variant={studentsFiltersOpen ? 'contained' : 'outlined'}
            startIcon={<FilterListIcon />}
            onClick={() => setStudentsFiltersOpen((o) => !o)}
            sx={{ height: 40 }}
          >
            {t('admin.users.filtersButton')}
          </Button>
          <Box sx={{ ml: isMobile ? 0 : 'auto' }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setIsCreateModalOpen(true)}
              sx={{ height: 40 }}
            >
              {t('users.create')}
            </Button>
          </Box>
        </Box>

        <Collapse in={studentsFiltersOpen}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>{t('contracts.status')}</InputLabel>
              <Select
                value={studentsFilters.contract_status ?? 'all'}
                label={t('contracts.status')}
                onChange={(e) => applyStudentsFilters({ contract_status: (e.target.value || 'all') as UserFilters['contract_status'] })}
              >
                <MenuItem value="all">{t('admin.users.all')}</MenuItem>
                <MenuItem value="active">{t('contracts.statusActive')}</MenuItem>
                <MenuItem value="suspended">{t('contracts.statusSuspended')}</MenuItem>
                <MenuItem value="completed">{t('contracts.statusCompleted')}</MenuItem>
                <MenuItem value="cancelled">{t('contracts.statusCancelled')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>{t('contracts.group')}</InputLabel>
              <Select
                value={studentsFilters.group_id ?? 'all'}
                label={t('contracts.group')}
                onChange={(e) => applyStudentsFilters({ group_id: e.target.value === 'all' ? undefined : Number(e.target.value) })}
              >
                <MenuItem value="all">{t('admin.users.all')}</MenuItem>
                {groups.map((g) => (
                  <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Collapse>

        {/* Список / таблица пользователей */}
        {isLoading ? (
          isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Card key={i}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <Skeleton variant="circular" width={48} height={48} sx={{ mr: 2 }} />
                      <Box sx={{ flexGrow: 1 }}>
                        <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
                        <Skeleton variant="text" width="80%" height={20} sx={{ mb: 1 }} />
                        <Skeleton variant="rectangular" width={90} height={24} sx={{ borderRadius: 1 }} />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('users.user')}</TableCell>
                    <TableCell>{t('common.email')}</TableCell>
                    <TableCell>{t('contracts.title')}</TableCell>
                    <TableCell>{t('contracts.group')}</TableCell>
                    <TableCell>{t('billing.subscriptionOrBalance', 'Абонемент / Баланс')}</TableCell>
                    <TableCell align="right">{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Skeleton variant="circular" width={32} height={32} />
                          <Skeleton variant="text" width={150} height={20} />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={200} height={20} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1 }} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={100} height={20} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={90} height={20} />
                      </TableCell>
                      <TableCell align="right">
                        <Skeleton variant="circular" width={32} height={32} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : (
          <>
            {isMobile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {users.map((user) => (
                  <UserCard
                    key={user.id}
                    user={user}
                    onView={(u) => navigate(`${ROUTES.USERS}/${u.id}`)}
                    onDelete={(u) => handleDelete(u.id)}
                  />
                ))}
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('users.user')}</TableCell>
                      <TableCell>{t('common.email')}</TableCell>
                      <TableCell>{t('contracts.title')}</TableCell>
                      <TableCell>{t('contracts.group')}</TableCell>
                      <TableCell>{t('billing.subscriptionOrBalance', 'Абонемент / Баланс')}</TableCell>
                      <TableCell align="right">{t('common.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 32, height: 32 }}>
                              <PersonIcon fontSize="small" />
                            </Avatar>
                            <MuiLink
                              component={RouterLink}
                              to={`${ROUTES.USERS}/${user.id}`}
                              underline="hover"
                              sx={{ fontWeight: 600 }}
                            >
                              {getUserDisplayName(user)}
                            </MuiLink>
                          </Box>
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          {user.contract ? (
                            <ContractStatusBadge
                              status={user.contract.status}
                              label={user.contract.status_label}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {t('users.noContract')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {user.contract?.group ? (
                            <MuiLink
                              component={RouterLink}
                              to={`${ROUTES.GROUPS}/${user.contract.group.id}`}
                              underline="hover"
                              sx={{ fontSize: 14 }}
                            >
                              {user.contract.group.name}
                            </MuiLink>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {user.contract?.finance_summary ? (
                            user.contract.finance_summary.active_grant ? (
                              <Typography variant="body2">
                                {t('billing.lessonsRemaining', '{{remaining}} из {{total}}', {
                                  remaining: user.contract.finance_summary.active_grant.lessons_remaining,
                                  total: user.contract.finance_summary.active_grant.lessons_total,
                                })}
                              </Typography>
                            ) : (
                              <Typography variant="body2">
                                {formatMoney(user.contract.finance_summary.balance_amount ?? 0, 'RUB')}
                              </Typography>
                            )
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={(e) => openMenu(e, user)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={closeMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={handleView}>
                <VisibilityIcon sx={{ mr: 1 }} fontSize="small" />
                {t('common.view')}
              </MenuItem>
              <MenuItem onClick={handleSendPasswordReset}>
                <LockResetIcon sx={{ mr: 1 }} fontSize="small" />
                {t('users.sendPasswordReset')}
              </MenuItem>
              <MenuItem
                onClick={() => menuUser && handleDelete(menuUser.id)}
                sx={{ color: 'error.main' }}
              >
                <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
                {t('users.delete')}
              </MenuItem>
            </Menu>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, flexWrap: 'wrap', width: '100%', minWidth: 0 }}>
              <TablePagination
                component="div"
                count={pagination.total}
                page={Math.max(0, (pagination.currentPage || 1) - 1)}
                onPageChange={(_, page) => fetchUsers({ page: page + 1, per_page: studentsPerPage, search: searchText.trim() || undefined, ...studentsFilters }).catch(() => {})}
                rowsPerPage={studentsPerPage}
                onRowsPerPageChange={(e) => {
                  const v = parseInt(e.target.value, 10)
                  setStudentsPerPage(v)
                  fetchUsers({ page: 1, per_page: v, search: searchText.trim() || undefined, ...studentsFilters }).catch(() => {})
                }}
                rowsPerPageOptions={[10, 15, 25, 50]}
                labelRowsPerPage={t('cards.rows_per_page')}
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}–${to} ${t('cards.of')} ${count !== -1 ? count : `${t('cards.more_than')} ${to}`}`
                }
                sx={{ overflow: 'hidden', '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', rowGap: 1 } }}
              />
            </Box>
          </>
        )}
        </>
        )}

        {tabValue === 1 && (
          <>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder={t('common.search')}
                value={inviteSearchText}
                onChange={(e) => setInviteSearchText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: isMobile ? 120 : 200, flex: isMobile ? 1 : undefined }}
              />
              <Box sx={{ ml: isMobile ? 0 : 'auto' }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<EmailIcon />}
                  onClick={() => setInviteModalOpen(true)}
                  sx={{ height: 40 }}
                >
                  {t('invitations.create')}
                </Button>
              </Box>
            </Box>
            {invitationsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <Skeleton variant="rectangular" width="100%" height={200} />
              </Box>
            ) : (
              <>
                {isMobile ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {invitations.map((inv) => (
                      <Card key={inv.id}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              {inv.status === 'used' && inv.used ? (
                                <MuiLink component={RouterLink} to={`${ROUTES.USERS}/${inv.used!.id}`} underline="hover" sx={{ fontWeight: 600 }}>
                                  {getUsedInvitationDisplay(inv)}
                                </MuiLink>
                              ) : (
                                <Typography variant="body1">{getUsedInvitationDisplay(inv)}</Typography>
                              )}
                              <Box sx={{ mt: 0.5 }}>
                                <Chip
                                  size="small"
                                  label={inv.status_label ?? inv.status}
                                  color={inv.status === 'used' ? 'default' : 'primary'}
                                  variant={inv.status === 'used' ? 'outlined' : 'filled'}
                                />
                              </Box>
                              {inv.status !== 'used' && (
                                <Button
                                  size="small"
                                  startIcon={<ContentCopyIcon />}
                                  onClick={() => copyToClipboard(inv.invitation_url)}
                                  sx={{ mt: 1 }}
                                >
                                  {t('invitations.copyLink')}
                                </Button>
                              )}
                            </Box>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation()
                                setInvMenuAnchor(e.currentTarget)
                                setInvMenuInvitation(inv)
                              }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                ) : (
                  <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('invitations.emailOrFio')}</TableCell>
                        <TableCell>{t('invitations.status')}</TableCell>
                        <TableCell>{t('invitations.invitationUrl')}</TableCell>
                        <TableCell align="right">{t('common.actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invitations.map((inv) => (
                        <TableRow key={inv.id}>
                          <TableCell>
                            {inv.status === 'used' && inv.used ? (
                              <MuiLink component={RouterLink} to={`${ROUTES.USERS}/${inv.used!.id}`} underline="hover" sx={{ fontWeight: 600 }}>
                                {getUsedInvitationDisplay(inv)}
                              </MuiLink>
                            ) : (
                              getUsedInvitationDisplay(inv)
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={inv.status_label ?? inv.status}
                              color={inv.status === 'used' ? 'default' : 'primary'}
                              variant={inv.status === 'used' ? 'outlined' : 'filled'}
                            />
                          </TableCell>
                          <TableCell>
                            {inv.status !== 'used' && (
                              <Button
                                size="small"
                                startIcon={<ContentCopyIcon />}
                                onClick={() => copyToClipboard(inv.invitation_url)}
                              >
                                {t('invitations.copyLink')}
                              </Button>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation()
                                setInvMenuAnchor(e.currentTarget)
                                setInvMenuInvitation(inv)
                              }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                )}
                <Menu
                  anchorEl={invMenuAnchor}
                  open={Boolean(invMenuAnchor)}
                  onClose={() => { setInvMenuAnchor(null); setInvMenuInvitation(null) }}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  {invMenuInvitation?.status === 'pending' && (
                    <>
                      {invMenuInvitation.email && (
                        <MenuItem
                          onClick={() => {
                            handleResendInvitation(invMenuInvitation!.id)
                            setInvMenuAnchor(null)
                            setInvMenuInvitation(null)
                          }}
                        >
                          <SendIcon sx={{ mr: 1 }} fontSize="small" />
                          {t('invitations.resend')}
                        </MenuItem>
                      )}
                      <MenuItem
                        onClick={() => {
                          handleCancelInvitation(invMenuInvitation!.id)
                          setInvMenuAnchor(null)
                          setInvMenuInvitation(null)
                        }}
                        sx={{ color: 'error.main' }}
                      >
                        <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
                        {t('invitations.cancel')}
                      </MenuItem>
                    </>
                  )}
                  {invMenuInvitation?.status === 'used' && invMenuInvitation.used && (
                    <MenuItem
                      component={RouterLink}
                      to={`${ROUTES.USERS}/${invMenuInvitation.used!.id}`}
                      onClick={() => { setInvMenuAnchor(null); setInvMenuInvitation(null) }}
                    >
                      <VisibilityIcon sx={{ mr: 1 }} fontSize="small" />
                      {t('common.view')}
                    </MenuItem>
                  )}
                </Menu>
                {invitationsPagination.total > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, flexWrap: 'wrap', width: '100%', minWidth: 0 }}>
                    <TablePagination
                      component="div"
                      count={invitationsPagination.total}
                      page={Math.max(0, invitationsPagination.page - 1)}
                      onPageChange={(_, page) => loadInvitations(page + 1)}
                      rowsPerPage={invitePerPage}
                      onRowsPerPageChange={(e) => {
                        const v = parseInt(e.target.value, 10)
                        setInvitePerPage(v)
                        loadInvitations(1, v)
                      }}
                      rowsPerPageOptions={[10, 15, 25, 50]}
                      labelRowsPerPage={t('words.rows_per_page', { defaultValue: 'На странице' })}
                      labelDisplayedRows={({ from, to, count }) =>
                        `${from}–${to} ${t('words.of', { defaultValue: 'из' })} ${count !== -1 ? count : to}`
                      }
                      sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}
                    />
                  </Box>
                )}
              </>
            )}
          </>
        )}

        {/* Модальное окно создания ученика */}
        <Dialog open={isCreateModalOpen} onClose={closeCreateModal} maxWidth="sm" fullWidth>
          <DialogTitle>{t('users.create')}</DialogTitle>
          <DialogContent>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
              {createFormError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => { setCreateFormError(null); setCreateFormSubscriptionError(false) }}>
                  {createFormError}
                  {createFormSubscriptionError && (
                    <Box sx={{ mt: 1 }}>
                      <Button component={RouterLink} to={ROUTES.PROFILE_SUBSCRIPTION} variant="outlined" size="small">
                        {t('subscription.goToSubscription')}
                      </Button>
                    </Box>
                  )}
                </Alert>
              )}
              <TextField
                fullWidth
                label={t('auth.surname')}
                required
                value={createForm.surname}
                onChange={(e) => setCreateForm((p) => ({ ...p, surname: e.target.value }))}
                sx={{ mt: 1 }}
              />
              <TextField
                fullWidth
                label={t('auth.firstName')}
                required
                value={createForm.first_name}
                onChange={(e) => setCreateForm((p) => ({ ...p, first_name: e.target.value }))}
                sx={{ mt: 2 }}
              />
              <TextField
                fullWidth
                label={t('auth.middleName')}
                value={createForm.middle_name}
                onChange={(e) => setCreateForm((p) => ({ ...p, middle_name: e.target.value }))}
                sx={{ mt: 2 }}
              />
              <TextField
                fullWidth
                label={t('users.nickname')}
                value={createForm.nickname}
                onChange={(e) => setCreateForm((p) => ({ ...p, nickname: e.target.value }))}
                sx={{ mt: 2 }}
                helperText={t('users.nicknameHint')}
              />
              <TextField
                fullWidth
                label={t('auth.email')}
                type="email"
                required
                value={createForm.email}
                onChange={(e) => setCreateForm((p) => ({ ...p, email: e.target.value }))}
                sx={{ mt: 2 }}
              />
              <TextField
                fullWidth
                label={t('auth.password')}
                type="password"
                required
                value={createForm.password}
                onChange={(e) => setCreateForm((p) => ({ ...p, password: e.target.value }))}
                sx={{ mt: 2 }}
                helperText={t('auth.validation.passwordMin')}
              />
              <DatePicker
                label={t('auth.birthday')}
                value={createForm.birthday ? dayjs(createForm.birthday) : null}
                onChange={(date) => setCreateForm((p) => ({ ...p, birthday: date ? date.format('YYYY-MM-DD') : '' }))}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: { mt: 2 },
                  },
                }}
              />
              <TextField
                fullWidth
                label={t('users.notes')}
                value={createForm.notes}
                onChange={(e) => setCreateForm((p) => ({ ...p, notes: e.target.value }))}
                sx={{ mt: 2 }}
                multiline
                rows={3}
                placeholder={t('contracts.notesPlaceholder')}
              />
            </LocalizationProvider>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={closeCreateModal}>{t('common.cancel')}</Button>
            <Button variant="contained" onClick={handleCreateStudent} disabled={createSubmitting}>
              {createSubmitting ? t('common.loading') : t('users.create')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Модальное окно создания приглашения */}
        <Dialog open={inviteModalOpen} onClose={closeInviteModal} maxWidth="sm" fullWidth>
          <DialogTitle>{t('invitations.create')}</DialogTitle>
          <DialogContent>
            {inviteError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setInviteError(null)}>
                {inviteError}
              </Alert>
            )}
            {createdInviteLink ? (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('invitations.createdCopy')}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={createdInviteLink}
                  InputProps={{ readOnly: true }}
                />
                <Button
                  startIcon={<ContentCopyIcon />}
                  onClick={() => copyToClipboard(createdInviteLink!)}
                  sx={{ mt: 1 }}
                >
                  {t('invitations.copyLink')}
                </Button>
              </Box>
            ) : (
              <>
                <TextField
                  fullWidth
                  label={t('common.email')}
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  sx={{ mt: 1 }}
                  helperText={t('invitations.emailOptional')}
                />
                {inviteEmail.trim() !== '' && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={inviteSendEmail}
                        onChange={(e) => setInviteSendEmail(e.target.checked)}
                      />
                    }
                    label={t('invitations.sendEmail')}
                    sx={{ mt: 2, display: 'block' }}
                  />
                )}
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            {createdInviteLink ? (
              <Button onClick={closeInviteModal}>{t('common.ok')}</Button>
            ) : (
              <>
                <Button onClick={closeInviteModal}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={handleCreateInvitation} disabled={inviteSubmitting}>
                  {inviteSubmitting ? t('common.loading') : t('invitations.create')}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  )
}
